import { useState, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  CarouselWrapper,
  CarouselListItem,
  CarouselList,
  CarouselOffsetWrapper,
} from './styled-components';
import { Dots } from './dots';
import type { CarouselHandle, CarouselProps, CarouselSlideSource } from './types';

export const Carousel = forwardRef<CarouselHandle, CarouselProps>(
  (
    {
      items,
      itemWidthPercent = 100,
      hideDots,
      onSlideChange,
      dotsActivated = true,
      onTouchStart,
      ...props
    },
    forwardedRef,
  ) => {
    const prevIndexRef = useRef(0);
    const slideSourceRef = useRef<CarouselSlideSource>('control');
    const [index, setIndex] = useState(0);
    const itemsWrapperRef = useRef<HTMLUListElement>(null);
    const itemsPerSlide = Math.floor(100 / itemWidthPercent);
    const slideCount = Math.ceil(items.length / itemsPerSlide);

    const setSlideSource = useCallback((source: CarouselSlideSource) => {
      slideSourceRef.current = source;
    }, []);

    const changeSlide = useCallback(
      ({
        relative,
        value,
        source,
      }: {
        relative: boolean;
        value: number;
        source: CarouselSlideSource;
      }) => {
        const container = itemsWrapperRef.current;
        let nextIndex;

        if (relative) {
          nextIndex = Math.min(Math.max(index + value, 0), slideCount - 1);
        } else {
          nextIndex = Math.min(Math.max(value, 0), slideCount - 1);
        }

        setSlideSource(source);
        container?.scrollTo({
          left: container.clientWidth * nextIndex,
          behavior: 'smooth',
        });
      },
      [slideCount, setSlideSource, index],
    );

    const handleScroll = useCallback(() => {
      const { scrollLeft = 0, clientWidth = 0 } = itemsWrapperRef.current ?? {};
      const slideWidth = Math.round((itemWidthPercent / 100) * itemsPerSlide * clientWidth);
      const nextIndex = Math.ceil(scrollLeft / slideWidth);

      if (nextIndex === prevIndexRef.current) {
        return;
      }

      setIndex(nextIndex);
      onSlideChange?.({
        currentIndex: nextIndex,
        prevIndex: prevIndexRef.current,
        slideSource: slideSourceRef.current,
      });

      prevIndexRef.current = nextIndex;
    }, [onSlideChange, itemWidthPercent, itemsPerSlide]);

    const handleTouchStart = useCallback<React.TouchEventHandler<HTMLElement>>(
      (event) => {
        setSlideSource('drag');
        onTouchStart?.(event);
      },
      [setSlideSource, onTouchStart],
    );

    useImperativeHandle(
      forwardedRef,
      (): CarouselHandle => ({
        nextSlide: () => {
          changeSlide({ relative: true, value: 1, source: 'control' });
        },
        prevSlide: () => {
          changeSlide({ relative: true, value: -1, source: 'control' });
        },
        slideCount,
      }),
      [slideCount, changeSlide],
    );

    useEffect(() => {
      const container = itemsWrapperRef.current;

      // Scroll listener added via ref handle in order to pass `passive` option for perf
      container?.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        container?.removeEventListener('scroll', handleScroll);
      };
    }, [handleScroll]);

    return (
      <CarouselWrapper>
        <CarouselOffsetWrapper>
          <CarouselList ref={itemsWrapperRef} onTouchStart={handleTouchStart} {...props}>
            {items?.map(({ key, node }) => (
              <CarouselListItem key={key} $itemWidthPercent={itemWidthPercent}>
                {node}
              </CarouselListItem>
            ))}
          </CarouselList>
        </CarouselOffsetWrapper>
        {!hideDots && slideCount > 1 && (
          <Dots
            count={slideCount}
            activeIndex={index}
            onDotClick={(i) => {
              changeSlide({ relative: false, value: i, source: 'dot' });
            }}
            dotsActivated={dotsActivated}
          />
        )}
      </CarouselWrapper>
    );
  },
);
